/**
 * Live precipitation radar from RainViewer — keyless, CORS-open, a composite of
 * national radar networks. Where there is radar coverage (most of Europe,
 * North America, parts of Asia and Australia) the map shows where it is
 * raining right now, as a TV weather map does; elsewhere, nothing.
 */

const INDEX = 'https://api.rainviewer.com/public/weather-maps.json';
/** RainViewer serves radar up to this zoom; finer views draw it scaled up. */
export const RADAR_MAX_ZOOM = 7;

export class Radar {
  private base: string | undefined;
  private fetchedAt = -Infinity;
  private readonly images = new Map<string, HTMLImageElement | HTMLCanvasElement | null>();
  version = 0;

  private refresh(): void {
    if (performance.now() - this.fetchedAt < 10 * 60_000) return;
    this.fetchedAt = performance.now();
    fetch(INDEX)
      .then(r => r.json())
      .then((index: { host: string; radar: { past: Array<{ path: string }> } }) => {
        const latest = index.radar.past[index.radar.past.length - 1];
        const next = `${index.host}${latest.path}`;
        if (next !== this.base) {
          this.base = next;
          this.images.clear();
          this.version++;
        }
      })
      .catch(() => { /* no radar is a valid answer */ });
  }

  /** The radar image for a tile, recoloured, if loaded; starts loading it if not. */
  tile(z: number, x: number, y: number): HTMLCanvasElement | undefined {
    this.refresh();
    if (this.base === undefined) return undefined;
    const key = `${z}/${x}/${y}`;
    const have = this.images.get(key);
    if (have instanceof HTMLCanvasElement) return have;
    if (have !== undefined) return undefined;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    // RainViewer serves one colour scheme, a monochrome blue; `1_1` asks for
    // smoothed pixels with snow marked. It is recoloured on arrival.
    img.src = `${this.base}/256/${z}/${x}/${y}/2/1_1.png`;
    img.onload = () => {
      this.images.set(key, recolour(img));
      this.version++;
    };
    img.onerror = () => { this.images.set(key, null); };
    this.images.set(key, img);
    if (this.images.size > 300) this.images.delete(this.images.keys().next().value as string);
    return undefined;
  }
}

/**
 * Broadcast colours for a map whose land is green: light rain in sky blue,
 * steady rain in deep blue, downpours in yellow through to red, snow in
 * white-lilac — the scheme TV forecasts use over green countryside, where
 * green rain would vanish into the fields. The source shades intensity from pale
 * to deep blue, so intensity is read back from how dark the blue is.
 */
function recolour(img: HTMLImageElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const px = data.data;
  const ramp: Array<[number, [number, number, number]]> = [
    [0, [150, 212, 255]], [0.35, [70, 150, 235]], [0.6, [36, 84, 205]], [0.8, [250, 200, 60]], [1, [228, 64, 50]],
  ];
  for (let i = 0; i < px.length; i += 4) {
    const a = px[i + 3];
    if (a === 0) continue;
    const r = px[i], g = px[i + 1], b = px[i + 2];
    // Snow comes through near-white; keep it pale lilac.
    if (r > 200 && g > 200 && b > 200) { px[i] = 236; px[i + 1] = 232; px[i + 2] = 255; px[i + 3] = 190; continue; }
    const intensity = Math.min(1, Math.max(0, 1 - (r + g) / 2 / 220));
    let k = 1;
    while (k < ramp.length - 1 && ramp[k][0] < intensity) k++;
    const [t0, c0] = ramp[k - 1];
    const [t1, c1] = ramp[k];
    const f = Math.min(1, Math.max(0, (intensity - t0) / (t1 - t0)));
    px[i] = c0[0] + (c1[0] - c0[0]) * f;
    px[i + 1] = c0[1] + (c1[1] - c0[1]) * f;
    px[i + 2] = c0[2] + (c1[2] - c0[2]) * f;
    px[i + 3] = Math.min(215, 90 + intensity * 150);
  }
  ctx.putImageData(data, 0, 0);
  return canvas;
}
